// Éléments partagés des écrans « chantiers » : onglets du module Interventions, mise en forme des heures et quantités,
// résumé du temps et du tableau d'effectif. Aucun pourcentage décoratif : des heures et des nombres d'étapes.
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { CalendarDays, Clock, GitCompareArrows, Users } from 'lucide-react';
import { date, nombre, pluriel } from '../../../lib/format.ts';
import { useSession } from '../../../lib/session.tsx';
import { Alerte, Badge, LienBouton } from '../../../components/ui.tsx';
import type { Effectif, Etape, ResumeEcart, VueChantier } from './types.ts';

/** Onglets du module Interventions : Ma journée · Chantiers · Planning (ceux qui affectent) · Dépannages. */
export function OngletsInterventions() {
  const { peut } = useSession();
  return (
    <nav className="onglets ch-onglets" aria-label="Sections des interventions">
      <NavLink to="/interventions/journee" className={({ isActive }) => `onglet ${isActive ? 'est-actif' : ''}`}>Ma journée</NavLink>
      <NavLink to="/interventions/chantiers" className={({ isActive }) => `onglet ${isActive ? 'est-actif' : ''}`}>Chantiers</NavLink>
      {peut('chantier.affecter') && <NavLink to="/interventions/planning" className={({ isActive }) => `onglet ${isActive ? 'est-actif' : ''}`}>Planning</NavLink>}
      <NavLink to="/interventions" end className={({ isActive }) => `onglet ${isActive ? 'est-actif' : ''}`}>Dépannages</NavLink>
    </nav>
  );
}

export const heures = (h: string | null | undefined) => (h === null || h === undefined ? null : `${nombre(h, 2)} h`);
export const quantite = (q: string, u: string) => `${nombre(q, 4)} ${u}`;

/** « 4 u × 2 h = 8 h » ou « temps à renseigner ». */
export function TempsEtape({ e }: { e: Etape }) {
  if (e.hours_planned === null) return <Badge ton="ambre">Temps à renseigner</Badge>;
  return (
    <span className="chiffre ch-temps-etape">
      {quantite(e.qty_planned, e.unit)}<span className="discret expert-seulement"> × {nombre(e.hours_per_unit, 4)} h</span> = <strong>{heures(e.hours_planned)}</strong>
    </span>
  );
}

export function ResumeTemps({ v }: { v: VueChantier }) {
  const t = v.temps;
  return (
    <div className="pile" style={{ gap: 'var(--e-2)' }}>
      <dl className="ch-chiffres">
        <div><dt><Clock aria-hidden /> Sur site</dt><dd className="chiffre">{heures(t.site)}</dd></div>
        <div><dt>Au bureau</dt><dd className="chiffre">{heures(t.bureau)}</dd></div>
        <div><dt>Étapes</dt><dd className="chiffre">{t.nb_etapes}</dd></div>
        {t.deplacements && t.deplacements.lignes > 0 && (
          <div><dt>Déplacements</dt><dd className="chiffre">{heures(t.deplacements.heures)}{t.deplacements.a_renseigner ? ' + à renseigner' : ''}</dd></div>
        )}
      </dl>
      {t.deplacements && t.deplacements.lignes > 0 && <p className="petit discret" style={{ margin: 0 }}>Les déplacements ne sont pas des étapes et ne comptent pas dans l’effectif.</p>}
      {t.incomplet && (
        <Alerte ton="ambre" titre={`Temps total incomplet : ${pluriel(t.etapes_a_renseigner, 'étape', 'étapes')} sans temps prévu`}>
          Le chantier peut être lancé ; le total ne compte que les heures connues.
        </Alerte>
      )}
    </div>
  );
}

/** Ce qui a été retenu (lecture) : « 2 personnes → 4 jours de travail ». */
export function ResumeEffectif({ e }: { e: Effectif }) {
  if (!e.disponible) return <p className="petit secondaire" style={{ margin: 0 }}>{e.raison}</p>;
  if (!e.retenu) return <p className="petit secondaire" style={{ margin: 0 }}>Aucun effectif retenu pour l’instant ({nombre(e.jours_personne, 2)} jours-personne).</p>;
  return (
    <div className="pile" style={{ gap: 'var(--e-2)' }}>
      <p className="ch-retenu" style={{ margin: 0 }}>
        <Users aria-hidden /> <strong>{pluriel(e.retenu.personnes, 'personne')}</strong>
        <span aria-hidden>→</span><CalendarDays aria-hidden /> <strong>{pluriel(e.retenu.jours, 'jour')} de travail</strong>
        <span className="petit discret">{e.retenu.mode === 'jours' ? '(nombre de jours imposé)' : '(effectif choisi)'}</span>
      </p>
      {e.retenu.alerte && <Alerte ton="ambre">{e.retenu.alerte}</Alerte>}
    </div>
  );
}

/** « 1 ligne ajoutée, 2 retirées, 1 quantité modifiée » (conception § 2.8) ; null si rien ne touche les étapes. */
export function phraseEcart(r: ResumeEcart) {
  const morceaux = [
    r.ajoutees ? `${pluriel(r.ajoutees, 'ligne ajoutée', 'lignes ajoutées')}` : null,
    r.retirees ? `${r.retirees} retirée${r.retirees > 1 ? 's' : ''}` : null,
    r.quantites ? `${pluriel(r.quantites, 'quantité modifiée', 'quantités modifiées')}` : null,
    r.heures ? `${pluriel(r.heures, 'temps modifié', 'temps modifiés')}` : null,
    r.libelles ? `${pluriel(r.libelles, 'libellé modifié', 'libellés modifiés')}` : null,
  ].filter(Boolean) as string[];
  if (!morceaux.length) return null;
  const premier = morceaux[0].replace(/^(\d+) retirée/, (_m, n) => `${n} ligne${Number(n) > 1 ? 's' : ''} retirée`);
  return [premier, ...morceaux.slice(1)].join(', ');
}

/**
 * Bandeau de révision (incrément 7) : une révision plus récente du devis est acceptée → « Voir et reporter » (qui prépare les
 * chantiers seulement : le technicien n'agit pas sur le devis). Émise mais pas encore acceptée : signalée, rien à reporter.
 */
export function BandeauRevision({ v, action }: { v: VueChantier; action?: ReactNode }) {
  const a = v.revision_a_reporter;
  if (a) {
    const phrase = phraseEcart(a.resume);
    // Le bouton est SOUS le texte (pas à côté) : sur téléphone, le titre garde toute la largeur et le bouton reste dans le premier écran.
    return (
      <Alerte ton="ambre" titre={`Révision ${a.rev_no} du devis acceptée le ${date(a.acceptee_le)}`}>
        <p style={{ margin: 0 }}>{phrase ? `${phrase}.` : 'Aucun changement ne touche les étapes du chantier.'}
          <span className="expert-seulement"> Rien n’a changé sur le chantier : vous choisissez ce que vous reportez ; une étape déjà faite n’est jamais retirée.</span></p>
        <div className="ch-bandeau-action">
          {action ?? <LienBouton icone={<GitCompareArrows aria-hidden />} to={`/interventions/chantiers/${v.chantier.id}/revision`}>Voir et reporter</LienBouton>}
        </div>
      </Alerte>
    );
  }
  const r = v.revision_plus_recente;
  if (!r || !v.droits.preparer) return null;
  return (
    <Alerte ton="info" titre={r.acceptee_le ? `La révision ${r.rev_no} du devis a été acceptée par le client` : `Une révision ${r.rev_no} du devis a été émise`} action={action}>
      {r.acceptee_le ? 'Ce chantier est terminé : sa structure ne se modifie plus.' : 'Rien à reporter tant que le client ne l’a pas acceptée (Études et devis › Marquer « accepté par le client »).'}
    </Alerte>
  );
}

/** Badges d'une étape touchée par une révision du devis (incrément 7). */
export function BadgesRevision({ ajoutee, retireeDuDevis, faite }: { ajoutee?: number | null; retireeDuDevis?: number | null; faite?: boolean }) {
  return (
    <>
      {ajoutee ? <Badge ton="info" icone={false}>Ajoutée (révision {ajoutee})</Badge> : null}
      {retireeDuDevis ? (faite
        ? <Badge ton="ambre" icone={false} titre="La ligne a été retirée du devis alors que l’étape était faite : elle reste, à arbitrer (travaux supplémentaires, avoir…).">Faite — retirée du devis (révision {retireeDuDevis}), à arbitrer</Badge>
        : <Badge ton="neutre" icone={false}>Retirée du devis (révision {retireeDuDevis})</Badge>) : null}
    </>
  );
}
